import React, { useEffect, useState } from 'react';
import { Modal as AntModal, Typography, Form, Row, Col, Input, Divider, Checkbox, Select } from 'antd';
import {
    Button,
    validateMessages,
} from '@presentation/components/form';
import style from './style';
import InputMask from 'react-input-mask';
import CheckCep from '@/services/viaCep';
import { CreateCustomerApi, UpdateCustomerApi } from '@/services/customerUpdateApi';
import { Notification } from '@presentation/components/common';
import customer from '@/@core/modules/customer/infra/container.registry';
import { SearchCustomerInterface } from '@/@core/modules/customer/domain/customer.entity';
import { useDebouncedCallback } from '@/@presentation/hooks/useDebouncedCallback';
import validator from 'validator';
import { useProcedure } from '@/@presentation/modules/procedure/context/procedure';
import { useBusinessPartner } from '@/@presentation/hooks/useBusinessPartner';
import { getNotFoundMessage } from '@/@presentation/constants/businessPartnerMessages';
import { normalizeCnpj, isCnpjLength, validateCNPJ } from '@/@core/utils/cnpj.utils';

export interface IReceiverProps {
    visible: boolean
    setVisible: React.Dispatch<React.SetStateAction<boolean>>
    setDoc?: React.Dispatch<React.SetStateAction<string>>
    doc?: string
    data?: any
    allData?: any[]
    onlyRead?: boolean
    callbackOnSave?: () => void
    callbackOnCancel?: () => void
    setReload: (arg: boolean) => void
    setItemBase: (arg: any) => void
    isFromCreationFlow?: boolean
    isFromNfProcess?: boolean
    onBPValidationResult?: (isValid: boolean) => void
}

export interface UpdateCustomerInterface {
    id?: number,
    personId?: number,
    isPayer?: boolean,
    scheduleId: number[],
    name: string
    email: string
    registry_code?: string
    code: string
    rne?: string
    nationality?: string
    address: {
        id?: number
        street: string
        number: string
        additional_details: string
        zipcode: string
        neighborhood: string
        city: string
        state: string
        complement?: string
    }
}

const Receiver = (props: IReceiverProps) => {
    const { states } = useProcedure();
    
    // Hook customizado para validação de Business Partner
    const { validateBusinessPartner } = useBusinessPartner();

    const isDisabled = props.data?.statusInvoice === 'Emitido' ||
        props.data?.statusInvoice === 'Solicitado' ||
        props?.data?.statusBilling === "paid" ||
        props?.data?.statusBilling === "pending" ||
        props?.data?.statusInvoice === 'Pendente Emissao'

    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(false)
    const [emailError, setEmailError] = useState(false);

    const [customerName, setCustomerName] = useState('')
    const [email, setEmail] = useState('')
    const [cep, setCep] = useState('')
    const [document, setDocument] = useState('')
    const [address, setAddress] = useState('')
    const [numberHome, setNumberHome] = useState('')
    const [complement, setComplement] = useState('')
    const [neighborhood, setNeighborhood] = useState('')
    const [state, setState] = useState('')
    const [city, setCity] = useState('')
    const [withoutCpf, setWithoutCpf] = useState(false)
    const [nationality, setNationality] = useState<string | null>(null)
    const [docNumber, setDocNumber] = useState('')

    const [searchedCustomer, setSearchedCustomer] = useState<SearchCustomerInterface>();

    const setStatesByCustomer = (data: SearchCustomerInterface) => {
        if (data.payment_customer) {
            setCustomerName(data.payment_customer.person?.name ?? '')
            setEmail(data.payment_customer.person?.email ?? '')
            setCep(data.payment_customer.address?.zipcode ?? '')
            props.setDoc && props.setDoc(data.payment_customer.person?.cpf ?? '')
            setAddress(data.payment_customer.address?.street ?? '')
            setNumberHome(data.payment_customer.address?.number ?? '')
            setComplement(data.payment_customer.address?.complement ?? '')
            setNeighborhood(data.payment_customer.address?.neighborhood ?? '')
            setState(data.payment_customer.address?.state ?? '')
            setCity(data.payment_customer.address?.city ?? '')
            
            // Converter nome completo da nacionalidade para sigla se necessário
            const customerNationality = data.payment_customer.person?.nationality?.toUpperCase()
            const nationalityOption = states.nationality?.find(option => 
                option.nome_pais.toUpperCase() === customerNationality || 
                option.sigla.toUpperCase() === customerNationality
            )
            setNationality(nationalityOption?.sigla ?? null)

            if (withoutCpf) {
                setDocument(data.payment_customer.person?.cpf ?? '')
            } else {
                setDocNumber(data.payment_customer?.person?.rne ?? '')
            }
        } else {
            setCustomerName('')
            setEmail('')
            setCep('')
            setAddress('')
            setNumberHome('')
            setComplement('')
            setNeighborhood('')
            setState('')
            setCity('')
            setNationality(null)
            if (withoutCpf) {
                setDocument('')
            } else {
                setDocNumber('')
            }
        }
    }

    const setCustumerByCpf = async (cpf: string) => {
        if (withoutCpf) return;

        const result = await customer.search.execute(cpf)

        if (result?.payment_customer) {
            setSearchedCustomer(result)
            setStatesByCustomer(result)
        } else {
            setSearchedCustomer(undefined)
            setCustomerName('')
            setEmail('')
            setCep('')
            setAddress('')
            setNumberHome('')
            setComplement('')
            setNeighborhood('')
            setState('')
            setCity('')
            setWithoutCpf(false)
            setNationality(null)
        }
    }


    const searchCustomerByCpf = async (initialCpf?: string) => {
        const formatCpf = normalizeCnpj(document)
        const formatInitialCpf = initialCpf ? normalizeCnpj(initialCpf) : undefined

        if (!initialCpf && !formatCpf) {
            return;
        }

        const isCPForCNPJ =
            formatCpf?.length === 11 ||
            formatCpf?.length === 14 ||
            formatInitialCpf?.length === 11 ||
            formatInitialCpf?.length === 14

        if (!isCPForCNPJ) {
            return;
        }

        if (formatInitialCpf) {
            await setCustumerByCpf(formatInitialCpf)
            return;
        }

        await setCustumerByCpf(formatCpf)
        return;

    }

    async function handleZipCode(zipcode: string) {
        if (zipcode?.length > 7) {
            const address = await CheckCep(zipcode)

            setAddress(address.logradouro ?? '')
            setNeighborhood(address?.bairro ?? '')
            setState(address.uf ?? '')
            setCity(address?.localidade ?? '')
        }
    }

    useEffect(() => {
        if (props.visible && states.nationality) {
            // Aguardar um pouco para garantir que o reset foi executado primeiro
            setTimeout(() => {
                // Se há paymentCustomer com dados completos (ID > 0), usar esses dados
                if (props?.data?.paymentCustomer && props?.data?.paymentCustomer?.id > 0) {
                    setCustomerName(props?.data?.paymentCustomer?.name ?? 'Não Informado')
                    setEmail(props?.data?.paymentCustomer?.email ?? '')
                    setCep(props?.data?.paymentCustomer?.zipcode ?? '')
                    setDocument(props?.data?.paymentCustomer?.registry_code ?? '')
                    props.setDoc && props.setDoc(props?.data?.paymentCustomer?.registry_code ?? '')
                    setAddress(props?.data?.paymentCustomer?.street ?? '')
                    setNumberHome(props?.data?.paymentCustomer?.number ?? '')
                    setComplement(props?.data?.paymentCustomer?.complement ?? '')
                    setNeighborhood(props?.data?.paymentCustomer?.neighborhood ?? '')
                    setState(props?.data?.paymentCustomer?.state ?? '')
                    setCity(props?.data?.paymentCustomer?.city ?? '')
                    
                    // Converter nome completo da nacionalidade para sigla se necessário
                    const customerNationality = props?.data?.paymentCustomer?.nationality?.toUpperCase()
                    const nationalityOption = states.nationality?.find(option => 
                        option.nome_pais.toUpperCase() === customerNationality || 
                        option.sigla.toUpperCase() === customerNationality
                    )
                    setNationality(nationalityOption?.sigla ?? null)
                    
                    // Auto-selecionar checkbox se nacionalidade não for Brasil e não tiver documento
                    const hasDocument = props?.data?.paymentCustomer?.registry_code
                    if (customerNationality && customerNationality !== 'BR' && customerNationality !== 'BRASIL' && !hasDocument) {
                        setWithoutCpf(true)
                        // Se é estrangeiro sem CPF, usar o RNE
                        setDocNumber(props?.data?.paymentCustomer?.rne ?? '')
                    } else {
                        setWithoutCpf(false)
                    }
                } else {
                    // Fallback: usar dados do paymentCustomer (mesmo com ID 0) e complementar com dados do paciente
                    const paymentCustomer = props?.data?.paymentCustomer || {}
                    
                    // Usar dados do paymentCustomer se disponíveis, senão usar dados do paciente
                    setCustomerName(
                        paymentCustomer?.name || 
                        props?.data?.patient || 
                        props?.data?.receiverName || 
                        'Não Informado'
                    )
                    setEmail(paymentCustomer?.email || props?.data?.patientEmail || '')
                    setCep(paymentCustomer?.zipcode || props?.data?.patientZipcode || props?.data?.addressZipCode || '')
                    setDocument(paymentCustomer?.registry_code || props?.data?.patientCpf || props?.data?.patientRegistryCode || '')
                    props.setDoc && props.setDoc(paymentCustomer?.registry_code || props?.data?.patientCpf || props?.data?.patientRegistryCode || '')
                    setAddress(paymentCustomer?.street || props?.data?.patientStreet || props?.data?.addressStreet || '')
                    setNumberHome(paymentCustomer?.number || props?.data?.patientNumber || props?.data?.addressNumber || '')
                    setComplement(paymentCustomer?.complement || props?.data?.patientComplement || props?.data?.addressComplement || '')
                    setNeighborhood(paymentCustomer?.neighborhood || props?.data?.patientNeighborhood || props?.data?.addressNeighborhood || '')
                    setState(paymentCustomer?.state || props?.data?.patientState || props?.data?.addressState || '')
                    setCity(paymentCustomer?.city || props?.data?.patientCity || props?.data?.addressCity || '')
                    
                    // Converter nome completo da nacionalidade para sigla se necessário
                    const customerNationality = (paymentCustomer?.nationality || paymentCustomer?.country || props?.data?.patientNationality || props?.data?.nationality)?.toUpperCase()
                    const nationalityOption = states.nationality?.find(option => 
                        option.nome_pais.toUpperCase() === customerNationality || 
                        option.sigla.toUpperCase() === customerNationality
                    )
                    setNationality(nationalityOption?.sigla ?? null)
                    
                    // Auto-selecionar checkbox se nacionalidade não for Brasil e não tiver documento
                    const hasDocument = paymentCustomer?.registry_code || props?.data?.patientCpf || props?.data?.patientRegistryCode
                    if (customerNationality && customerNationality !== 'BR' && customerNationality !== 'BRASIL' && !hasDocument) {
                        setWithoutCpf(true)
                        // Se é estrangeiro sem CPF, usar o RNE
                        setDocNumber(paymentCustomer?.rne || props?.data?.patientRne || props?.data?.patientInternationalDocument || '')
                    } else {
                        setWithoutCpf(false)
                    }
                }
            }, 100)
        }
    }, [props.visible, props.data, states.nationality])

    const handleChangeDocument = (e: any) => {
        const { value } = e.target;
        // Aceita letras, dígitos e separadores de máscara; normaliza removendo . / -
        const normalized = normalizeCnpj(value.replace(/[^A-Za-z0-9.\-\/]/g, ''));
        setDocument(normalized);
        props.setDoc && props.setDoc(normalized);
        setError(false);
    };

    // Formata o valor normalizado (sem separadores) para exibição progressiva.
    // Detecta CPF (11 dígitos puros) ou CNPJ (>11 chars ou qualquer letra).
    const formatDocumentForDisplay = (normalized: string): string => {
        if (!normalized) return '';
        const hasLetters = /[A-Z]/.test(normalized);
        if (hasLetters || normalized.length > 11) {
            // CNPJ: XX.XXX.XXX/XXXX-XX
            const n = normalized.slice(0, 14);
            if (n.length <= 2) return n;
            if (n.length <= 5) return `${n.slice(0, 2)}.${n.slice(2)}`;
            if (n.length <= 8) return `${n.slice(0, 2)}.${n.slice(2, 5)}.${n.slice(5)}`;
            if (n.length <= 12) return `${n.slice(0, 2)}.${n.slice(2, 5)}.${n.slice(5, 8)}/${n.slice(8)}`;
            return `${n.slice(0, 2)}.${n.slice(2, 5)}.${n.slice(5, 8)}/${n.slice(8, 12)}-${n.slice(12, 14)}`;
        }
        // CPF: XXX.XXX.XXX-XX
        const n = normalized.slice(0, 11);
        if (n.length <= 3) return n;
        if (n.length <= 6) return `${n.slice(0, 3)}.${n.slice(3)}`;
        if (n.length <= 9) return `${n.slice(0, 3)}.${n.slice(3, 6)}.${n.slice(6)}`;
        return `${n.slice(0, 3)}.${n.slice(3, 6)}.${n.slice(6, 9)}-${n.slice(9, 11)}`;
    };

    const resetFormStates = () => {
        setCustomerName('')
        setEmail('')
        setCep('')
        setDocument('')
        setAddress('')
        setNumberHome('')
        setComplement('')
        setNeighborhood('')
        setState('')
        setCity('')
        setWithoutCpf(false)
        setNationality(null)
        setDocNumber('')
        setSearchedCustomer(undefined)
        setError(false)
        setEmailError(false)
        props.setDoc && props.setDoc('')
    }

    const handleCancel = () => {
        resetFormStates()
        if (props?.callbackOnCancel !== undefined) {
            props.callbackOnCancel();
        } else {
            props.setVisible(false);
        }
    }

    const searchCustomerByRne = async (value: string) => {
            const result = await customer.searchByRne.execute(value)

            if (!result) {
                setSearchedCustomer(undefined)
                setCustomerName('')
                setEmail('')
                setCep('')
                setAddress('')
                setNumberHome('')
                setComplement('')
                setNeighborhood('')
                setState('')
                setCity('')

                return;
            }

            setSearchedCustomer(result)
            setStatesByCustomer(result)
    }

    const handleChangeRne = useDebouncedCallback(async (value: string) => {
        if (withoutCpf) {
            await searchCustomerByRne(value)
        }
    }, 1000)

    // validateCNPJ centralizado em @core/utils/cnpj.utils — suporta formato numérico e alfanumérico

    const validateEmail = (email: string): boolean => {
        return validator.isEmail(email);
    };

    async function validateCPF(strCPF: string): Promise<boolean> {
        strCPF = strCPF.replace(/\D/g, '')

        if (strCPF === '00000000000' || strCPF === '11111111111' || strCPF === '22222222222' || strCPF === '33333333333' ||
            strCPF === '44444444444' || strCPF === '55555555555' || strCPF === '66666666666' || strCPF === '77777777777' ||
            strCPF === '88888888888' || strCPF === '99999999999' || strCPF?.length !== 11) {
            return false;
        }

        let Soma = 0;
        for (let i = 1; i <= 9; i++) {
            Soma = Soma + parseInt(strCPF.substring(i - 1, i)) * (11 - i);
        }

        let Resto = (Soma * 10) % 11;
        if (Resto === 10 || Resto === 11) {
            Resto = 0;
        }

        if (Resto !== parseInt(strCPF.substring(9, 10))) {
            return false;
        }

        Soma = 0;
        for (let k = 1; k <= 10; k++) {
            Soma = Soma + parseInt(strCPF.substring(k - 1, k)) * (12 - k);
        }

        Resto = (Soma * 10) % 11;
        if (Resto === 10 || Resto === 11) {
            Resto = 0;
        }

        if (Resto !== parseInt(strCPF.substring(10, 11))) {
            return false;
        }

        return true;
    }

    async function handleUpdateCustomer(skipCallbacks: boolean = false) {
        if (customerName?.length === 0 || email?.length === 0 || cep?.length < 7 || (withoutCpf && nationality?.length === 0)) {
            Notification.error({
                message: 'Por favor, verifique os dados informados. Para pacientes estrangeiros sem CPF, é necessário preencher todos os campos obrigatórios, com exceção do CPF/CNPJ.',
                duration: 3,
            });
            setError(true);
            return false;
        }

        // Validar se paciente estrangeiro sem CPF não tem nacionalidade brasileira
        if (withoutCpf && nationality === 'BR') {
            Notification.error({
                message: 'Pacientes estrangeiros sem CPF não podem ter nacionalidade brasileira!',
                duration: 3,
            });
            setError(true);
            return false;
        }

        if (withoutCpf === false) {
            const docNormalizado = normalizeCnpj(document);
            const validadeCpfOuCnpj = isCnpjLength(docNormalizado) ? validateCNPJ(document) : await validateCPF(document)

            if (!validadeCpfOuCnpj || document?.length < 10) {
                Notification.error({
                    message: 'O CPF/CNPJ informado não é valido!',
                    duration: 3,
                });
                return false;
            }
        }

        setError(false);

        const newCustomer: UpdateCustomerInterface = {
            id: searchedCustomer?.payment_customer?.id || undefined,
            scheduleId: props?.allData?.map(i => +i.id) || [props?.data?.id],
            name: customerName,
            email: email,
            registry_code: document,
            code: document,
            rne: docNumber,
            nationality: nationality || undefined,
            address: {
                street: address,
                number: numberHome,
                additional_details: complement,
                zipcode: cep,
                neighborhood: neighborhood,
                city: city,
                state: state,
                complement: complement,
            },
        };

        if (searchedCustomer?.payment_customer?.id) {
            newCustomer.id = searchedCustomer.payment_customer.id;
        }

        if (searchedCustomer?.payment_customer?.address?.id) {
            newCustomer.address.id = searchedCustomer.payment_customer.address.id;
        }

        if (searchedCustomer?.payment_customer?.person?.id) {
            newCustomer.personId = searchedCustomer.payment_customer.person.id;
        }

        let resCustomerId = 0;

        if (!newCustomer.id) {
            const result = await CreateCustomerApi(newCustomer);
            if (result) {
                resCustomerId = result;
            }
        } else {
            resCustomerId = await UpdateCustomerApi(newCustomer);
        }

        if (resCustomerId) {
            Notification.success({
                message: 'Tomador atualizado com sucesso!',
                duration: 2,
            });

            const nItemBase = { ...props.data };
            nItemBase.paymentCustomer = {
                ...nItemBase.paymentCustomer,
                id: resCustomerId,
                name: customerName,
                email: email,
                registry_code: document,
                code: document,
                rne: docNumber,
                nationality: nationality,
                city: city,
                state: state,
                street: address,
                number: numberHome,
                neighborhood: neighborhood,
                zipcode: cep,
                complement: complement,
                address: {
                    ...newCustomer.address
                },
            };

            props.setItemBase(nItemBase);
            
            // Se não deve pular callbacks, executa o fluxo normal
            if (!skipCallbacks) {
                props.setVisible(false);

                if (props?.callbackOnSave) {
                    props.callbackOnSave();
                }

                props.setReload(true);
            }
            
            return true;
        } else {
            Notification.error({
                message: 'Não foi possível atualizar os dados do tomador nesse momento!',
                duration: 3,
            });
            return false;
        }
    }

    useEffect(() => {
        if (props.visible) {
            // Reset form when modal opens to ensure fresh data
            resetFormStates()
            
            if (props?.data?.paymentCustomer?.registry_code) {
                searchCustomerByCpf(props?.data?.paymentCustomer?.registry_code)
            }
            if (props?.data?.paymentCustomer?.rne) {
                searchCustomerByRne(props?.data?.paymentCustomer?.rne)
                setWithoutCpf(true)
            }
        }
    }, [props.visible]);


    useEffect(() => {
        if (props.visible) {
            searchCustomerByCpf()
        }
    }, [document]);

    return (
        <AntModal
            data-testid="modal-form"
            destroyOnClose={true}
            forceRender
            title={
                <Typography.Title style={style.description}>
                    {props.onlyRead ? 'Dados do Tomador' : 'Informe os dados do tomador'}
                </Typography.Title>
            }
            visible={props.visible}
            centered={true}
            onOk={() => handleCancel()}
            onCancel={() => handleCancel()}
            footer={[
                <Button
                    text={props.onlyRead ? 'Voltar' : 'Cancelar'}
                    key="cancelar"
                    nativeProps={{
                        ghost: true,
                    }}
                    loading={loading}
                    onClick={() => handleCancel()}
                />,
                !props.onlyRead && (<Button
                    text='Salvar'
                    key="salvar"
                    size="middle"
                    disabled={isDisabled}
                    loading={loading}
                    nativeProps={{
                        htmlType: 'submit',
                    }}
                    onClick={async () => {
                        // Prevenir múltiplos cliques
                        if (loading) return;
                        
                        setLoading(true);
                        try {
                            const documentoLimpo = normalizeCnpj(document ?? '');
                            const isCNPJ = isCnpjLength(documentoLimpo);
                            const needsSAPValidation = (props.isFromCreationFlow || props.isFromNfProcess) && !withoutCpf && isCNPJ;
                            
                            const saved = await handleUpdateCustomer(needsSAPValidation);
                            
                            if (!saved) {
                                setLoading(false);
                                return;
                            }
                            
                            if (needsSAPValidation) {
                                const scheduleId = props?.allData?.[0]?.id || props?.data?.id;
                                const isValidBusinessPartner = await validateBusinessPartner({
                                    name: customerName,
                                    document: document,
                                    scheduleId: scheduleId,
                                    options: {
                                        totalSelected: props?.allData?.length,
                                        showNotification: false,
                                    }
                                });
                                
                                if (props.isFromNfProcess && props.onBPValidationResult) {
                                    props.onBPValidationResult(isValidBusinessPartner);
                                }
                                
                                // Se for da página de NF e BP inválido, mostra erro mas não cancela
                                if (!isValidBusinessPartner && props.isFromNfProcess) {
                                    const errorMsg = getNotFoundMessage();
                                    Notification.error({
                                        message: errorMsg.message,
                                        description: errorMsg.description,
                                        duration: errorMsg.duration,
                                    });
                                    props.setVisible(false);
                                    if (props?.callbackOnSave) {
                                        props.callbackOnSave();
                                    }
                                    props.setReload(true);
                                } else if (!isValidBusinessPartner && props.isFromCreationFlow && !props.isFromNfProcess) {
                                    const errorMsg = getNotFoundMessage();
                                    Notification.error({
                                        message: errorMsg.message,
                                        description: errorMsg.description,
                                        duration: errorMsg.duration,
                                    });
                                    props.setVisible(false);
                                    if (props?.callbackOnCancel) {
                                        props.callbackOnCancel();
                                    }
                                } else if (isValidBusinessPartner) {
                                    props.setVisible(false);
                                    if (props?.callbackOnSave) {
                                        props.callbackOnSave();
                                    }
                                    props.setReload(true);
                                }
                            }                            
                        } finally {
                            setLoading(false);
                        }
                    }}
                />)
            ]}
        >
            <Form
                layout="horizontal"
                validateMessages={validateMessages}
                style={{
                    textAlign: 'center',
                }}
            >
                <Row style={{ marginTop: -30, marginBottom: 0, paddingBottom: 0 }}>
                    <Col span={12} style={{ padding: 10, marginBottom: 0, paddingBottom: 0 }}>
                        <Form.Item
                            label="CPF/CNPJ"
                            labelCol={{ span: 24 }}
                        >
                            <Input
                                disabled={isDisabled || props.onlyRead || withoutCpf}
                                onChange={handleChangeDocument}
                                style={{
                                    width: '100%',
                                    borderRadius: 8,
                                    height: 40,
                                    borderStyle: 'solid',
                                    borderWidth: 1,
                                    paddingLeft: 10,
                                    borderColor: error && !withoutCpf ? '#ff0000' : '#D9D9D9'
                                }}
                                value={formatDocumentForDisplay(document)}
                                maxLength={18}
                                placeholder="CPF ou CNPJ"
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row>
                    <Checkbox 
                        style={{ marginTop: -27, marginLeft: 10 }} 
                        checked={withoutCpf === true} 
                        onChange={(e) => {
                            const isChecked = e.target.checked;
                            setWithoutCpf(isChecked);
                            
                            if (!isChecked) {
                                // Limpar campos quando desmarcar
                                setNationality(null);
                                setDocNumber('');
                            }
                        }}
                    >
                        <Typography style={{ marginTop: 4, fontSize: 12 }}>
                            Paciente estrangeiro sem CPF
                        </Typography>
                    </Checkbox>
                </Row>
                {
                    withoutCpf === true && (
                        <Row>
                            <Col span={14} style={{ padding: 20, paddingLeft: 10, paddingBottom: 0 }}>
                                <Form.Item
                                    label="RNE/Passaporte"
                                    labelCol={{ span: 24 }}
                                >
                                    <Input
                                        disabled={isDisabled || props.onlyRead}
                                        status='warning'
                                        style={{
                                            height: 40,
                                            marginTop: -15,
                                            textTransform: 'uppercase',
                                            borderColor: error ? '#ff0000' : '#D9D9D9'
                                        }}
                                        placeholder="Informe o Nº"
                                        onChange={(e) => {
                                            setDocNumber(e.target.value)
                                            handleChangeRne(e.target.value)
                                        }}
                                        value={docNumber}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={10} style={{ padding: 20, paddingBottom: 0 }}>
                                <Form.Item
                                    label="Nacionalidade"
                                    labelCol={{ span: 24 }}
                                >
                                    <Select
                                        disabled={isDisabled || props.onlyRead}
                                        style={{
                                            height: 40,
                                            marginTop: -15,
                                            borderColor: error ? '#ff0000' : '#D9D9D9'
                                        }}
                                        placeholder="Selecione a nacionalidade"
                                        showSearch
                                        filterOption={(input, option: any) => {
                                            return (
                                                option.value?.toLowerCase().indexOf(input?.toLowerCase()) >= 0 ||
                                                option.label?.toLowerCase().indexOf(input?.toLowerCase()) >= 0 ||
                                                option.children?.toLowerCase().indexOf(input?.toLowerCase()) >= 0
                                            );
                                        }}
                                        onChange={(value) => setNationality(value)}
                                        value={nationality}
                                    >
                                        {states.nationality
                                            ?.filter(option => option.sigla !== 'BR')
                                            .map((option) => (
                                                <Select.Option key={option.sigla} value={option.sigla}>
                                                    {option.nome_pais}
                                                </Select.Option>
                                            ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    )
                }
                <Row style={{ marginLeft: -10 }}>
                    <Col span={24} style={{ padding: 20, paddingTop: 0 }}>
                        <Form.Item
                            label="Nome"
                            labelCol={{ span: 24 }}
                        >
                            <Input
                                disabled={isDisabled || props.onlyRead}
                                style={{
                                    height: 40,
                                    marginTop: -15,
                                    textTransform: 'uppercase',
                                    borderColor: error ? '#ff0000' : '#D9D9D9'
                                }}
                                placeholder="Informe o Nome"
                                onChange={(e) => setCustomerName(e.target.value)}
                                value={customerName}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row style={{ marginTop: -60, marginLeft: -10 }}>
                    <Col span={24} style={{ padding: 20 }}>
                        <Form.Item
                            label="E-mail"
                            labelCol={{ span: 24 }}
                            validateStatus={emailError ? 'error' : ''}
                            help={emailError ? 'Por favor, insira um e-mail válido (exemplo@dominio.com)' : ''}
                        >
                            <Input
                                disabled={isDisabled || props.onlyRead}
                                style={{
                                    height: 40,
                                    marginTop: -15,
                                    textTransform: 'lowercase',
                                    borderColor: error ? '#ff0000' : '#D9D9D9'
                                }}
                                placeholder="Informe o e-mail"
                                onChange={(e) => {
                                    const emailValue = e.target.value;
                                    setEmail(emailValue);
                                    setEmailError(emailValue ? !validateEmail(emailValue) : false);
                                }}
                                onBlur={(e) => {
                                    const emailValue = e.target.value;
                                    setEmailError(emailValue ? !validateEmail(emailValue) : false);
                                }}
                                value={email}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider style={{ marginTop: -20 }} />

                <Row style={{ marginTop: -30, marginLeft: -10 }}>
                    <Col span={4} style={{ padding: 20 }}>
                        <Form.Item
                            label="CEP"
                            labelCol={{ span: 24 }}
                        >
                            <InputMask
                                disabled={isDisabled || props.onlyRead}
                                mask="99999-999"
                                maskChar="_"
                                onChange={(e) => setCep(e.target.value)}
                                onBlur={async (e) => await handleZipCode(e.target.value)}
                                value={cep}
                                style={{
                                    marginTop: -15,
                                    borderRadius: 8,
                                    height: 40,
                                    borderStyle: 'solid',
                                    borderWidth: 1,
                                    paddingLeft: 10,
                                    borderColor: error ? '#ff0000' : '#D9D9D9'
                                }}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row style={{ marginTop: -60, marginLeft: -10 }}>
                    <Col span={17} style={{ padding: 20 }}>
                        <Form.Item
                            label="Rua"
                            labelCol={{ span: 24 }}
                        >
                            <Input
                                disabled={isDisabled || props.onlyRead}
                                style={{
                                    height: 40,
                                    marginTop: -15,
                                    textTransform: 'uppercase',
                                    borderColor: error ? '#ff0000' : '#D9D9D9'
                                }}
                                placeholder="Informe a rua"
                                onChange={(e) => setAddress(e.target.value)}
                                value={address}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={7} style={{ padding: 20 }}>
                        <Form.Item
                            label="Número"
                            labelCol={{ span: 24 }}
                        >
                            <Input
                                disabled={isDisabled || props.onlyRead}
                                style={{
                                    height: 40,
                                    width: 100,
                                    marginTop: -15,
                                    textTransform: 'uppercase',
                                    borderColor: error ? '#ff0000' : '#D9D9D9'
                                }}
                                placeholder="Número"
                                onChange={(e) => setNumberHome(e.target.value)}
                                value={numberHome}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row style={{ marginTop: -60, marginLeft: -10 }}>
                    <Col span={24} style={{ padding: 20 }}>
                        <Form.Item
                            label="Bairro"
                            labelCol={{ span: 24 }}
                        >
                            <Input
                                disabled={isDisabled || props.onlyRead}
                                style={{
                                    height: 40,
                                    textTransform: 'uppercase',
                                    marginTop: -15
                                }}
                                placeholder="Informe o bairro"
                                onChange={(e) => setNeighborhood(e.target.value)}
                                value={neighborhood}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row style={{ marginTop: -60, marginLeft: -10 }}>
                    <Col span={12} style={{ padding: 20 }}>
                        <Form.Item
                            label="Cidade"
                            labelCol={{ span: 24 }}
                        >
                            <Input
                                disabled={isDisabled || props.onlyRead}
                                style={{
                                    height: 40,
                                    textTransform: 'uppercase',
                                    marginTop: -15
                                }}
                                placeholder="Informe a cidade"
                                onChange={(e) => setCity(e.target.value)}
                                value={city}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12} style={{ padding: 20 }}>
                        <Form.Item
                            label="Estado"
                            labelCol={{ span: 24 }}
                        >
                            <Input
                                disabled={isDisabled || props.onlyRead}
                                style={{
                                    height: 40,
                                    textTransform: 'uppercase',
                                    marginTop: -15
                                }}
                                placeholder="Informe o estado"
                                onChange={(e) => setState(e.target.value)}
                                value={state}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row style={{ marginTop: -60, marginLeft: -10 }}>
                    <Col span={24} style={{ padding: 20 }}>
                        <Form.Item
                            label="Complemento"
                            labelCol={{ span: 24 }}
                        >
                            <Input
                                disabled={isDisabled || props.onlyRead}
                                style={{
                                    height: 40,
                                    textTransform: 'uppercase',
                                    marginTop: -15
                                }}
                                placeholder="Informe o complemento"
                                onChange={(e) => setComplement(e.target.value)}
                                value={complement}
                            />
                        </Form.Item>
                    </Col>
                </Row>

            </Form>

        </AntModal>
    );
};

export { Receiver };
